/**
 * SessionGuard.jsx
 *
 * Two runtime guards that run during market hours:
 *
 * 1. PLAN REMINDER — every 30 min, a modal shows the committed day plan
 *    in large text. Dismiss (holds until next 30-min mark) or Snooze (5 min).
 *
 * 2. WARNING TIME BLOCK — at a user-set time ET, a full-screen overlay locks
 *    the UI. To continue, the user must type "I ACCEPT THE RISK" and enter
 *    their current P&L. If the loss exceeds the configured threshold, the
 *    engine is force-stopped and stays stopped until the next market session.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { dayPlanGet } from '../lib/api';
import { renderNote } from './alerts/FirstDecisionGate';

// Current ET time — used for 30-min market-hour reminder boundaries
function etNow() {
  const str = new Date().toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour12: false, hour: '2-digit', minute: '2-digit' });
  const [h, m] = str.split(':').map(Number);
  return { h, m, hhmm: str };
}

// Current SGT time — used for the user-set warning time (user is in Singapore)
function sgtNow() {
  const str = new Date().toLocaleTimeString('en-US', { timeZone: 'Asia/Singapore', hour12: false, hour: '2-digit', minute: '2-digit' });
  return str; // "HH:MM"
}

// "HH:MM" string → minutes since midnight
function toMins(hhmm) {
  if (!hhmm) return -1;
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

// Floor the current minute to the last :00 or :30 boundary → key like "10:30"
function current30Mark() {
  const { h, m } = etNow();
  return `${String(h).padStart(2,'0')}:${m < 30 ? '00' : '30'}`;
}

const PLAN_LABELS = {
  TRADE:        { label: 'Trade it',        color: 'var(--up)' },
  WAIT_CONFIRM: { label: 'Wait for confirm', color: 'var(--warn)' },
  SIT_OUT:      { label: 'Sit out',          color: 'var(--down)' },
};

export default function SessionGuard({ engineOn, stopEngine, marketOpen, globalSettings }) {
  // ── Plan reminder state ───────────────────────────────────────────────────
  const [reminderVisible,  setReminderVisible]  = useState(false);
  const [plan,             setPlan]             = useState(null); // { plan, note }
  const [lastShownMark,    setLastShownMark]    = useState('');   // "HH:MM" of last shown 30-min mark
  const snoozedUntilRef = useRef(0); // epoch ms
  const planCache       = useRef(null);

  // ── Warning block state ───────────────────────────────────────────────────
  const [warningVisible,   setWarningVisible]   = useState(false);
  const [warningAccepted,  setWarningAccepted]  = useState(false); // accepted THIS session
  const [acceptInput,      setAcceptInput]      = useState('');
  const [pnlInput,         setPnlInput]         = useState('');
  const [pnlError,         setPnlError]         = useState('');
  const warningDateRef = useRef(''); // ET date string when warning was shown — one per day

  const warningTime     = globalSettings?.warningTime     || '';  // "HH:MM" ET
  const warningMessage  = globalSettings?.warningMessage  || 'You have reached your scheduled review time. Assess your session before continuing.';
  const maxDailyLoss    = Number(globalSettings?.maxDailyLoss || 0); // positive $, e.g. 500

  // ── Fetch day plan (cached) ───────────────────────────────────────────────
  const fetchPlan = useCallback(async () => {
    if (planCache.current) return planCache.current;
    try {
      const d = await dayPlanGet();
      planCache.current = d;
      return d;
    } catch { return null; }
  }, []);

  // ── Master clock tick — runs every 30s ───────────────────────────────────
  useEffect(() => {
    const tick = async () => {
      if (!marketOpen) return;

      const now  = Date.now();
      const mark  = current30Mark();           // ET boundaries for plan reminder
      const sgtHHMM = sgtNow();                // SGT for user-set warning time

      // ── 1. Plan reminder ────────────────────────────────────────────────
      if (
        !reminderVisible &&
        mark !== lastShownMark &&
        now > snoozedUntilRef.current
      ) {
        const d = await fetchPlan();
        // Only show if a plan was committed today
        if (d?.plan && d?.date === etDateStr()) {
          setPlan(d);
          setReminderVisible(true);
          setLastShownMark(mark);
        }
      }

      // ── 2. Warning time block ───────────────────────────────────────────
      if (
        warningTime &&
        sgtHHMM >= warningTime &&
        !warningAccepted &&
        warningDateRef.current !== etDateStr()
      ) {
        setWarningVisible(true);
      }
    };

    tick(); // immediate on mount / dependency change
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [marketOpen, reminderVisible, lastShownMark, warningTime, warningAccepted, fetchPlan]);

  // ── Reminder actions ──────────────────────────────────────────────────────
  const dismissReminder = () => setReminderVisible(false);
  const snoozeReminder  = () => {
    snoozedUntilRef.current = Date.now() + 5 * 60 * 1000; // 5 min
    setReminderVisible(false);
  };

  // ── Warning accept ────────────────────────────────────────────────────────
  const submitWarning = () => {
    if (acceptInput.trim() !== 'I ACCEPT THE RISK') {
      setPnlError('Type exactly: I ACCEPT THE RISK');
      return;
    }
    const pnl = parseFloat(pnlInput.replace(/[$,\s]/g, ''));
    if (isNaN(pnl)) {
      setPnlError('Enter your current P&L (e.g. -350 or 120)');
      return;
    }

    // Hard loss limit: force stop and block session
    if (maxDailyLoss > 0 && pnl < 0 && Math.abs(pnl) >= maxDailyLoss) {
      stopEngine?.();
      setWarningVisible(false);
      setWarningAccepted(false); // stays blocked — will re-show as "session ended"
      warningDateRef.current = etDateStr();
      // Show a permanent session-ended overlay (different state)
      setSessionEnded(true);
      return;
    }

    setWarningVisible(false);
    setWarningAccepted(true);
    warningDateRef.current = etDateStr();
    setAcceptInput('');
    setPnlInput('');
    setPnlError('');
  };

  // ── Session ended (loss threshold breached) ───────────────────────────────
  const [sessionEnded, setSessionEnded] = useState(false);

  // Clear session ended flag at next day / market re-open
  useEffect(() => {
    if (sessionEnded && !marketOpen) {
      // Market closed — reset for tomorrow
      const id = setTimeout(() => setSessionEnded(false), 60_000);
      return () => clearTimeout(id);
    }
  }, [sessionEnded, marketOpen]);

  const planOpt = plan ? PLAN_LABELS[plan.plan] : null;

  // ── Nothing to show ───────────────────────────────────────────────────────
  if (!reminderVisible && !warningVisible && !sessionEnded) return null;

  return (
    <>
      {/* ── Plan reminder modal ─────────────────────────────────────────── */}
      {reminderVisible && (
        <div style={backdrop} onClick={dismissReminder}>
          <div style={{ ...modal, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--faint)', marginBottom: 12 }}>
              30-minute check-in
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>
              Today's committed plan
            </div>
            {planOpt && (
              <div style={{ fontSize: 38, fontWeight: 900, color: planOpt.color, letterSpacing: '.02em', marginBottom: 10, lineHeight: 1.1 }}>
                {planOpt.label}
              </div>
            )}
            {plan?.note && (
              <div style={{ marginBottom: 20, lineHeight: 1.6, padding: '12px 16px', borderRadius: 6, background: 'var(--bg)', border: '1px solid var(--border)' }}>
                {renderNote(plan.note, { fontSize: 15, gap: 8 })}
              </div>
            )}
            <div style={{ fontSize: 12, color: 'var(--faint)', marginBottom: 24, lineHeight: 1.5 }}>
              Is your current position consistent with this plan? If not, act now.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={snoozeReminder} style={{ ...mBtn, color: 'var(--muted)', borderColor: 'var(--border)' }}>
                Snooze 5 min
              </button>
              <button onClick={dismissReminder} style={{ ...mBtn, background: planOpt?.color ?? 'var(--accent)', color: 'var(--bg)', borderColor: 'transparent', fontWeight: 800 }}>
                Understood — dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Session ended (loss limit hit) ─────────────────────────────── */}
      {sessionEnded && (
        <div style={{ ...backdrop, background: 'rgba(0,0,0,0.92)', zIndex: 9999 }}>
          <div style={{ ...modal, maxWidth: 440, textAlign: 'center', borderColor: 'var(--down)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🛑</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--down)', marginBottom: 10, letterSpacing: '.02em' }}>
              Session Ended
            </div>
            <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.6, marginBottom: 8 }}>
              Your loss exceeded the <strong style={{ color: 'var(--down)' }}>${maxDailyLoss}</strong> daily limit.
              The alert engine has been stopped.
            </div>
            <div style={{ fontSize: 12, color: 'var(--faint)', lineHeight: 1.5 }}>
              Trading is locked for this session. The dashboard will reset when the market next opens.
              Step away, review your trades, and come back tomorrow.
            </div>
          </div>
        </div>
      )}

      {/* ── Warning time block ──────────────────────────────────────────── */}
      {warningVisible && !sessionEnded && (
        <div style={{ ...backdrop, background: 'rgba(0,0,0,0.88)', zIndex: 9998 }}>
          <div style={{ ...modal, maxWidth: 520, borderColor: 'var(--warn)' }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--warn)', marginBottom: 14 }}>
              ⚠ Scheduled session review — {warningTime} SGT
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', lineHeight: 1.55, marginBottom: 20 }}>
              {warningMessage}
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
                What is your current P&L for this session? ($)
              </div>
              <input
                type="text"
                value={pnlInput}
                onChange={e => { setPnlInput(e.target.value); setPnlError(''); }}
                placeholder="e.g. -350 or 120"
                style={{ ...warnInput, borderColor: pnlError ? 'var(--down)' : 'var(--border)' }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
                To continue, type exactly: <strong style={{ color: 'var(--warn)', letterSpacing: '.05em' }}>I ACCEPT THE RISK</strong>
              </div>
              <input
                type="text"
                value={acceptInput}
                onChange={e => { setAcceptInput(e.target.value); setPnlError(''); }}
                placeholder="I ACCEPT THE RISK"
                style={{ ...warnInput, fontWeight: 700, letterSpacing: '.04em', borderColor: pnlError ? 'var(--down)' : 'var(--border)' }}
              />
            </div>

            {pnlError && (
              <div style={{ fontSize: 11, color: 'var(--down)', marginBottom: 14 }}>
                ⚠ {pnlError}
              </div>
            )}

            {maxDailyLoss > 0 && (
              <div style={{ fontSize: 11, color: 'var(--faint)', marginBottom: 20, padding: '8px 12px', borderRadius: 5, background: 'var(--bg)', lineHeight: 1.5 }}>
                If your loss is ${maxDailyLoss} or more, trading will be locked for the rest of this session regardless.
              </div>
            )}

            <button
              onClick={submitWarning}
              disabled={acceptInput.trim() !== 'I ACCEPT THE RISK' || !pnlInput.trim()}
              style={{
                width: '100%', padding: '13px', borderRadius: 6, border: 'none', cursor: 'pointer',
                fontWeight: 800, fontSize: 14, letterSpacing: '.04em',
                background: acceptInput.trim() === 'I ACCEPT THE RISK' && pnlInput.trim() ? 'var(--warn)' : 'var(--border)',
                color: acceptInput.trim() === 'I ACCEPT THE RISK' && pnlInput.trim() ? 'var(--bg)' : 'var(--faint)',
                transition: 'all .2s',
              }}
            >
              Continue trading
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function etDateStr() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
}

// ── Styles ────────────────────────────────────────────────────────────────────
const backdrop = {
  position: 'fixed', inset: 0, zIndex: 9000,
  background: 'rgba(0,0,0,0.72)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 20,
};

const modal = {
  background: 'var(--bg-elev, #1a1a1a)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: '28px 28px 24px',
  width: '100%',
  boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
};

const mBtn = {
  padding: '10px 18px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600,
  letterSpacing: '.02em',
};

const warnInput = {
  width: '100%', boxSizing: 'border-box',
  padding: '10px 12px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--bg)', color: 'var(--text)',
  fontSize: 14, fontFamily: "'IBM Plex Mono', monospace",
};
